export const money = (v) =>
  v === null || v === undefined || v === ""
    ? null
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

export const titleCase = (s) =>
  (s || "").toString().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const FINANCIAL_ROLES = ["owner", "admin", "inventory_manager", "sales"];
export const canSeeFinancials = (role) => FINANCIAL_ROLES.includes(role);

export const MANAGE_WATCHES = ["owner", "admin", "inventory_manager"];
export const MANAGE_LEADS = ["owner", "admin", "sales", "va"];
export const MANAGE_REQUESTS = ["owner", "admin", "sales"];
export const MANAGE_SELLERS = ["owner", "admin", "inventory_manager", "sales"];
export const MANAGE_TASKS = ["owner", "admin", "sales", "va"];
export const MANAGE_DOSSIERS = ["owner", "admin", "inventory_manager"];
export const MANAGE_MERCH = ["owner", "admin", "content_manager"];
export const MANAGE_CONTENT = ["owner", "admin", "content_manager"];
export const MANAGE_JOURNAL = ["owner", "admin", "content_manager"];
export const can = (role, list) => list.includes(role);

export const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  va: "VA",
  sales: "Sales",
  inventory_manager: "Inventory Manager",
  content_manager: "Content Manager",
  view_only: "View Only",
};

export const TIER_LABELS = {
  lch_access: "LCH Access",
  collector_circle: "Collector Circle",
  patron_client: "Patron Client",
  platinum_table: "Platinum Table",
  founders_ledger: "Founders Ledger",
};

export const TIER_ORDER = [
  "lch_access", "collector_circle", "patron_client", "platinum_table", "founders_ledger",
];

export const WATCH_STATUSES = [
  "available", "reserved", "sold", "incoming", "authentication_pending", "private_drop", "hidden",
];

export const AUTH_STATUSES = ["not_started", "pending", "passed", "failed"];

export const LEAD_STATUSES = ["new", "contacted", "qualified", "nurturing", "won", "lost"];
export const REQUEST_STATUSES = ["new_request", "in_progress", "sourced", "fulfilled", "closed"];
export const SELLER_STATUSES = ["new_seller_intake", "reviewing", "accepted", "declined", "converted"];

export const STATUS_COLORS = {
  available: "bg-emerald-100 text-emerald-800 border-emerald-200",
  reserved: "bg-amber-100 text-amber-800 border-amber-200",
  sold: "bg-zinc-200 text-zinc-700 border-zinc-300",
  incoming: "bg-blue-100 text-blue-800 border-blue-200",
  authentication_pending: "bg-purple-100 text-purple-800 border-purple-200",
  private_drop: "bg-indigo-100 text-indigo-800 border-indigo-200",
  hidden: "bg-zinc-100 text-zinc-500 border-zinc-200",
  passed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  pending: "bg-purple-100 text-purple-800 border-purple-200",
  not_started: "bg-zinc-100 text-zinc-500 border-zinc-200",
  new: "bg-blue-100 text-blue-800 border-blue-200",
  new_request: "bg-blue-100 text-blue-800 border-blue-200",
  new_seller_intake: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  sourced: "bg-indigo-100 text-indigo-800 border-indigo-200",
  fulfilled: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-zinc-200 text-zinc-700 border-zinc-300",
  contacted: "bg-sky-100 text-sky-800 border-sky-200",
  qualified: "bg-violet-100 text-violet-800 border-violet-200",
  nurturing: "bg-orange-100 text-orange-800 border-orange-200",
  reviewing: "bg-amber-100 text-amber-800 border-amber-200",
  won: "bg-emerald-100 text-emerald-800 border-emerald-200",
  lost: "bg-red-100 text-red-800 border-red-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  declined: "bg-red-100 text-red-800 border-red-200",
};

export function scoreColor(score) {
  if (score >= 70) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (score >= 45) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-zinc-100 text-zinc-600 border-zinc-200";
}

export function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

// ---- Public form option sets ----
export const BRANDS = [
  "Rolex", "Patek Philippe", "Audemars Piguet", "Omega", "Cartier", "Tudor",
  "Vacheron Constantin", "A. Lange & S\u00f6hne", "Jaeger-LeCoultre", "IWC",
  "Panerai", "Breitling", "Grand Seiko", "Richard Mille",
];

export const BUDGET_OPTIONS = [
  "Under $5,000", "$5,000 - $15,000", "$15,000 - $50,000", "$50,000 - $150,000", "$150,000+",
];

export const TIMELINE_OPTIONS = ["Ready now", "Within 30 days", "Within 90 days", "Just researching"];

export const INTEREST_OPTIONS = [
  "Investment grade", "First luxury watch", "Active collector", "A meaningful gift", "Enthusiast / researching",
];

export const CONTACT_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone call" },
  { value: "text", label: "Text message" },
];

// ---- Merch ----
export const MERCH_CATEGORIES = [
  { value: "apparel", label: "Apparel" },
  { value: "accessories", label: "Accessories" },
  { value: "leather_goods", label: "Leather Goods" },
  { value: "watch_care", label: "Watch Care" },
];

export const merchCategoryLabel = (v) =>
  (MERCH_CATEGORIES.find((c) => c.value === v) || {}).label || titleCase(v);

export const TIER_RANK = {
  lch_access: 1, collector_circle: 2, patron_client: 3, platinum_table: 4, founders_ledger: 5,
};
export const tierRank = (key) => TIER_RANK[key] || 0;

// ---- Journal ----
export const JOURNAL_CATEGORIES = ["Guides", "Collecting", "Brand Spotlights", "Market"];

// ---- Parts ----
export const MANAGE_PARTS = ["owner", "admin", "inventory_manager"];

export const PART_CATEGORIES = [
  { value: "dial", label: "Dial" },
  { value: "bezel", label: "Bezel" },
  { value: "insert", label: "Insert" },
  { value: "bracelet", label: "Bracelet" },
  { value: "strap", label: "Strap" },
  { value: "clasp", label: "Clasp" },
  { value: "link", label: "Link" },
  { value: "crown", label: "Crown" },
  { value: "pusher", label: "Pusher" },
  { value: "hands", label: "Hands" },
  { value: "crystal", label: "Crystal" },
  { value: "movement", label: "Movement" },
  { value: "movement_component", label: "Movement Component" },
  { value: "case", label: "Case" },
  { value: "caseback", label: "Caseback" },
  { value: "buckle", label: "Buckle" },
  { value: "box", label: "Box" },
  { value: "papers", label: "Papers / Manual" },
  { value: "tool_accessory", label: "Tool / Accessory" },
  { value: "other", label: "Other" },
];
export const partCategoryLabel = (v) =>
  (PART_CATEGORIES.find((c) => c.value === v) || {}).label || titleCase(v);

export const PART_STATUSES = ["available", "reserved", "sold_out", "hidden"];
export const PART_ORIGINS = [
  { value: "oem", label: "OEM / Factory" },
  { value: "aftermarket", label: "Aftermarket" },
];
export const partOriginLabel = (v) =>
  (PART_ORIGINS.find((o) => o.value === v) || {}).label || (v ? titleCase(v) : "");
export const PART_CONDITIONS = [
  "New", "Old Stock (NOS)", "Used - Mint", "Used - Excellent", "Used - Good", "For Parts / Repair",
];
